import { classNames } from '@fullstory/util/fmt';
import React, { forwardRef } from 'react';
import { identifyComponent } from '../../util';
import { Box } from '../Box';
import cssColors from './pane.color.module.scss';
import css, { ClassNames } from './pane.module.scss';

type elevationScale = 0 | 1 | 2 | 3;
type radiusScale = 0 | 2 | 4 | 6 | 8 | 12;
type boxStyleRadiusScale = [radiusScale, radiusScale, radiusScale, radiusScale];

type BorderRadiusProps = Pick<
  PaneBaseProps,
  | 'borderRadius'
  | 'borderRadiusTopLeft'
  | 'borderRadiusTopRight'
  | 'borderRadiusBottomRight'
  | 'borderRadiusBottomLeft'
>;

const getRadiusProps = (props: BorderRadiusProps): BorderRadiusProps => {
  const { borderRadius } = props;

  if (typeof borderRadius === 'number') {
    return { borderRadius };
  }
  if (borderRadius) {
    const [tr, tl, bl, br] = borderRadius;
    return {
      borderRadiusTopLeft: tl,
      borderRadiusTopRight: tr,
      borderRadiusBottomRight: br,
      borderRadiusBottomLeft: bl,
    };
  }
  return props;
};

type borderTypes = 'standard' | 'none';

export interface PaneBaseProps {
  testId?: string;
  elevation?: elevationScale;
  borderRadius?: radiusScale | boxStyleRadiusScale;
  borderRadiusTopLeft?: radiusScale;
  borderRadiusTopRight?: radiusScale;
  borderRadiusBottomRight?: radiusScale;
  borderRadiusBottomLeft?: radiusScale;
  border?: borderTypes;
  borderTop?: borderTypes;
  borderRight?: borderTypes;
  borderLeft?: borderTypes;
  borderBottom?: borderTypes;
  backgroundColor?: keyof typeof cssColors;
}

export type PaneProps = React.ComponentPropsWithoutRef<typeof Box> & PaneBaseProps;

/**
 * The pane component is an extension of the Box component so you can add dimension to an area of
 * your application.
 */
export const Pane = forwardRef<HTMLDivElement, PaneProps>((props, ref) => {
  const {
    testId,
    className,
    backgroundColor,
    elevation,
    borderRadius,
    borderRadiusTopLeft,
    borderRadiusTopRight,
    borderRadiusBottomRight,
    borderRadiusBottomLeft,
    border,
    borderTop,
    borderRight,
    borderLeft,
    borderBottom,
    children,
    ...rest
  } = props;
  const {
    borderRadius: bRad,
    borderRadiusTopLeft: bRadTL,
    borderRadiusTopRight: bRadTR,
    borderRadiusBottomRight: bRadBR,
    borderRadiusBottomLeft: bRadBL,
  } = getRadiusProps(props);

  const conditionalClasses = {
    [css[`elevation_${elevation}` as ClassNames]]: !!elevation,
    [css[`bRad_${bRad}px` as ClassNames]]: !!bRad,
    [css[`bRadTL_${bRadTL}px` as ClassNames]]: !!bRadTL,
    [css[`bRadTR_${bRadTR}px` as ClassNames]]: !!bRadTR,
    [css[`bRadBR_${bRadBR}px` as ClassNames]]: !!bRadBR,
    [css[`bRadBL_${bRadBL}px` as ClassNames]]: !!bRadBL,
    [css[`b_${border}` as ClassNames]]: !!border,
    [css[`bt_${borderTop}` as ClassNames]]: !!borderTop,
    [css[`br_${borderRight}` as ClassNames]]: !!borderRight,
    [css[`bb_${borderBottom}` as ClassNames]]: !!borderBottom,
    [css[`bl_${borderLeft}` as ClassNames]]: !!borderLeft,
  };

  const cssColorClass = backgroundColor ? cssColors[backgroundColor] : 'default';

  return (
    <Box
      data-component="Pane"
      testId={testId ? testId : 'pane'}
      className={classNames(css.paneComponent, className, cssColorClass, conditionalClasses)}
      ref={ref}
      {...rest}
      {...identifyComponent('pane', rest)}
    >
      {children}
    </Box>
  );
});

Pane.displayName = 'Pane';
